<script setup>
import { ref, computed, toRefs, watch, onMounted } from 'vue';
import { useMutation } from '@vue/apollo-composable';

import CreateProfilePictureUpload from '@/gql/user/CreateProfilePictureUpload.gql';
import UpdateUserProfile from '@/gql/user/UpdateUserProfile.gql';

import AppModal from '@/components/general/Modal.vue';
import AppFileInput from '@/components/general/FileInput.vue';
// import AppProfileImageInput from '@/components/profile/ProfileImageInput.vue'
// import { handleError } from '@/services/utils';
// import { hasAnyDirectives } from '@apollo/client/utilities';

const photoInput = ref(null);

// If there is a selected item, it means we are editing an existing library
const props = defineProps({
  selectedItem: {
    type: Object,
    required: true,
  },
});
const { selectedItem } = toRefs(props);

const emit = defineEmits(['success']);
const visible = defineModel('visible', { required: true });

const {
  formData,
  resetData,
  saveButtonDisabled,
  onSubmit,
  error,
  saving,
  uploading,
  latestVariables,
} = useEditProfile();

watch(visible, () => {
  resetData();
});

onMounted(() => {
  resetData();
});

function useEditProfile() {
  const saving = ref(false);
  const error = ref(null);
  const photoUploading = ref(false);

  const formData = ref({
    first_name: '',
    last_name: '',
    picture: null,
  });

  // Mutation to get upload link and save the profile picture
  const {
    mutate: createProfilePictureUpload,
    loading: createProfilePictureLoading,
    onDone: onCreateProfilePictureDone,
  } = useMutation(CreateProfilePictureUpload, {
    onError: (err) => {
      error.value = err;
    },
  });
  // Mutation to update user profile
  const { mutate, loading, onDone } = useMutation(UpdateUserProfile, {
    onError: (err) => {
      error.value = err;
    },
  });

  const saveButtonDisabled = computed(
    () =>
      saving.value ||
      loading.value ||
      createProfilePictureLoading.value ||
      !formData.value.first_name?.length ||
      !formData.value.last_name?.length
  );

  function resetData() {
    const {
      first_name: firstName = '',
      last_name: lastName = '',
      picture: picture = null,
    } = selectedItem.value || {};
    formData.value = {
      first_name: firstName,
      last_name: lastName,
      picture: picture,
    };
    error.value = null;
    photoUploading.value = false;
  }

  // First save the profile picture (if it exists), then save the profile name details
  function onSubmit() {
    error.value = false;
    saving.value = true;
    const { picture } = formData.value;
    if (picture && typeof picture === 'object') {
      photoUploading.value = true;
      const variables = {
        contentType: picture.type,
      };
      createProfilePictureUpload(variables);
    } else {
      saveProfile();
    }
  }

  onCreateProfilePictureDone(async (resp) => {
    try {
      const signedUrl = resp && resp.data.create_profile_picture_upload;
      await photoInput.value.upload(signedUrl);
      saveProfile();
      photoUploading.value = false;
    } catch (err) {
      // handleError(err);
      error.value = err;
      saving.value = false;
    }
  });

  const latestVariables = computed(() => {
    const { first_name, last_name, picture } = formData.value;
    const variables = {
      firstName: first_name,
      lastName: last_name,
    };
    if (!picture) {
      variables.setProfilePictureNull = true;
    }

    return variables;
  });
  function saveProfile() {
    mutate(latestVariables.value);
    onDone(() => {
      saving.value = false;
      resetData();
      emit('success', true);
    });
  }

  const uploading = computed(
    () =>
      (photoUploading.value || createProfilePictureLoading.value) &&
      !error.value
  );

  return {
    formData,
    saveButtonDisabled,
    resetData,
    onSubmit,
    error,
    saving,
    uploading,
    latestVariables,
  };
}
</script>

<template>
  <app-modal
    v-model:visible="visible"
    header="Edit profile"
    :closeOnEscape="!photoInput?.isPreviewOpen"
    :api-query="UpdateUserProfile"
    :api-variables="latestVariables"
    @after-hide="resetData"
  >
    <Message v-if="error" severity="error" closable>{{
      error.message
    }}</Message>
    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-2">
        <label for="photo">Photo</label>
        <app-file-input
          ref="photoInput"
          :disabled="saving"
          accept="image/jpeg,image/png"
          v-model="formData.picture"
          :uploading="uploading"
          round-preview
        />
        <!-- <app-profile-image-input
          ref="photoInput"
          data-id="photo"
          :user="selectedItem"
          v-model="formData.picture"
          :uploading="uploading"
        /> -->
      </div>

      <div class="flex flex-col gap-2">
        <label for="first_name"
          >First name <span class="text-primary-500">*</span></label
        >
        <InputText
          data-id="first_name"
          :disabled="saving"
          v-model="formData.first_name"
          placeholder="e.g. Jan"
        />
      </div>
      <div class="flex flex-col gap-2">
        <label for="last_name"
          >Last name <span class="text-primary-500">*</span></label
        >
        <InputText
          data-id="last_name"
          :disabled="saving"
          v-model="formData.last_name"
          placeholder="e.g. Arul"
        />
      </div>
      <div class="flex flex-col gap-2">
        <label for="email">Email</label>
        <InputText data-id="email" :value="selectedItem.email" disabled />
      </div>
    </div>
    <template #footer>
      <Button
        :disabled="saveButtonDisabled"
        @click="onSubmit"
        label="Save"
        :icon="saving ? 'icon-[prime--spinner] animate-spin' : ''"
        fluid
        size="large"
      ></Button>
    </template>
  </app-modal>
</template>

<style scoped></style>
